import { useState } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import type { TaskQueueItem } from '../lib/tasks'
import { HelpDot } from './guidance/Guidance'

/* Never-dead-end fallback (Gap 2, Ale ruling 2026-07-22).
 *
 * A task type with no dedicated pane used to land on a disabled skeleton
 * stub — a silent dead end. This card makes the task VISIBLE (type,
 * description, raw payload) and keeps the generic backend verbs
 * clickable: POST /tasks/{id}/resolve and /tasks/{id}/close. The backend
 * is the authority on what it accepts — a refusal (409/422) surfaces
 * here honestly instead of the button pretending nothing exists.
 */

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

/** Officer wording for types that have no dedicated screen yet. The raw
 *  enum ("REQUEST_UNLOCKED_COPY") was the last place the machine layer was
 *  visible to an officer (atlas p5, 2026-07-28). */
const FALLBACK_TYPE_LABELS: Record<string, string> = {
  request_unlocked_copy: 'unlocked copy to request',
  daily_digest: 'daily summary',
  verification_remediation: 'verification to restore',
  request_overdue: 'request overdue',
  chain_orphan: 'chain head to confirm',
}

function taskTypeLabel(taskType: string): string {
  return FALLBACK_TYPE_LABELS[taskType] ?? taskType.replace(/_/g, ' ')
}

export default function UnroutablePane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  async function call(verb: 'resolve' | 'close') {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/tasks/${task.task_id}/${verb}`, {
        method: 'POST',
        body: verb === 'resolve' ? JSON.stringify(note.trim() ? { note } : {}) : undefined,
      })
      setDone(verb === 'resolve' ? 'Resolved — recorded under your name.' : 'Closed.')
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}>
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div
        className="vcard"
        data-testid="unroutable-task"
        style={{ flex: 1, padding: 0, overflow: 'hidden' }}
      >
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
            {task.title || 'Task without a dedicated screen'} <HelpDot k="pane.unroutable" />
          </div>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
            This kind of task has no dedicated screen yet. Everything recorded about it is available
            below — nothing is hidden.
          </div>
        </div>
        <div style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: 'var(--text-12)', marginBottom: 8 }}>
            <span className="tag">{taskTypeLabel(task.task_type)}</span>
            {task.fund_name && (
              <span style={{ marginLeft: 10, color: 'var(--muted)' }}>{task.fund_name}</span>
            )}
          </div>
          {task.description && (
            <div
              style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', whiteSpace: 'pre-wrap' }}
            >
              {task.description}
            </div>
          )}
          {task.task_data != null && (
            <details>
              <summary
                style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', cursor: 'pointer' }}
              >
                Technical detail
              </summary>
              <pre
                data-testid="unroutable-payload"
                style={{
                  marginTop: 10,
                  fontSize: 'var(--text-12)',
                  background: 'var(--paper, #f8f7f4)',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: 10,
                  overflowX: 'auto',
                  maxHeight: 320,
                }}
              >
                {JSON.stringify(task.task_data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>

      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="vcard" style={{ padding: 18 }}>
          {error && (
            <div
              data-testid="unroutable-error"
              style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 10 }}
            >
              {error}
            </div>
          )}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note recorded with the resolution"
            rows={2}
            style={{
              width: '100%',
              fontSize: 'var(--text-12)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: 8,
              resize: 'vertical',
              marginBottom: 10,
            }}
          />
          <button
            className="vbtn pri"
            data-testid="unroutable-resolve"
            disabled={busy}
            style={{ width: '100%' }}
            onClick={() => call('resolve')}
          >
            Resolve
          </button>
          <button
            className="vbtn"
            data-testid="unroutable-close"
            disabled={busy}
            style={{ width: '100%', marginTop: 8 }}
            onClick={() => call('close')}
          >
            Close (acknowledge)
          </button>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 8 }}>
            These call the generic backend verbs. If the backend cannot resolve this type, its
            refusal shows here — the task never disappears silently.
          </div>
        </div>
      </div>
    </div>
  )
}
